import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  Thermometer, Droplets, Wind, Volume2, Radio, AlertTriangle, CheckCircle,
  Plus, RefreshCw, Loader2, Wifi, WifiOff, Settings, Activity,
  Bell, BellOff, Gauge, Clock, MapPin, Building, Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const SENSOR_ICONS = {
  temperature: Thermometer,
  humidity: Droplets,
  air_quality: Wind,
  noise: Volume2,
  gas: AlertTriangle,
  vibration: Activity,
  pressure: Gauge,
  motion: Radio,
  wearable: Activity,
  equipment: Settings,
  custom: Radio
};

const SENSOR_COLORS = {
  temperature: 'text-red-500 bg-red-100',
  humidity: 'text-blue-500 bg-blue-100',
  air_quality: 'text-green-500 bg-green-100',
  noise: 'text-purple-500 bg-purple-100',
  gas: 'text-orange-500 bg-orange-100',
  vibration: 'text-pink-500 bg-pink-100',
  equipment: 'text-gray-500 bg-gray-100'
};

const STATUS_COLORS = {
  online: 'bg-green-100 text-green-800',
  offline: 'bg-gray-100 text-gray-800',
  warning: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800'
};

const IoTSensorDashboard = () => {
  const { toast } = useToast();
  const [dashboard, setDashboard] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [sensorHistory, setSensorHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [newSensor, setNewSensor] = useState({
    sensor_id: '',
    name: '',
    type: 'temperature',
    location: '',
    department: '',
    unit: '',
    min_threshold: '',
    max_threshold: ''
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [dashRes, alertsRes] = await Promise.all([
        axios.get(`${API}/api/sensors/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/sensors/alerts?acknowledged=false`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDashboard(dashRes.data);
      setSensors(dashRes.data.current_readings || []);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSensorHistory = async (sensorId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/sensors/data/${sensorId}?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSensorHistory(response.data.readings || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  useEffect(() => {
    if (selectedSensor) {
      fetchSensorHistory(selectedSensor.sensor_id);
    }
  }, [selectedSensor]);

  const handleRegisterSensor = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const sensorData = {
        ...newSensor,
        min_threshold: newSensor.min_threshold ? parseFloat(newSensor.min_threshold) : null,
        max_threshold: newSensor.max_threshold ? parseFloat(newSensor.max_threshold) : null
      };
      
      await axios.post(`${API}/api/sensors/register`, sensorData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Sensor Registered', description: 'New sensor has been registered successfully' });
      setShowRegisterDialog(false);
      setNewSensor({
        sensor_id: '', name: '', type: 'temperature', location: '',
        department: '', unit: '', min_threshold: '', max_threshold: ''
      });
      fetchDashboard();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to register sensor',
        variant: 'destructive'
      });
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/api/sensors/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Alert Acknowledged' });
      fetchDashboard();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to acknowledge alert', variant: 'destructive' });
    }
  };

  const formatValue = (value, unit) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}${unit || ''}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="iot-sensor-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IoT Sensor Dashboard</h1>
          <p className="text-gray-500">Real-time environmental and safety monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchDashboard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Register Sensor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Register New Sensor</DialogTitle>
                <DialogDescription>Add a new IoT sensor to the monitoring system</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sensor ID *</Label>
                  <Input
                    placeholder="e.g., TEMP-001"
                    value={newSensor.sensor_id}
                    onChange={(e) => setNewSensor({ ...newSensor, sensor_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Name *</Label>
                  <Input
                    placeholder="e.g., Warehouse Temperature"
                    value={newSensor.name}
                    onChange={(e) => setNewSensor({ ...newSensor, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={newSensor.type} onValueChange={(v) => setNewSensor({ ...newSensor, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="temperature">Temperature</SelectItem>
                      <SelectItem value="humidity">Humidity</SelectItem>
                      <SelectItem value="air_quality">Air Quality</SelectItem>
                      <SelectItem value="noise">Noise Level</SelectItem>
                      <SelectItem value="gas">Gas Detection</SelectItem>
                      <SelectItem value="vibration">Vibration</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input
                    placeholder="e.g., °C, dB, ppm"
                    value={newSensor.unit}
                    onChange={(e) => setNewSensor({ ...newSensor, unit: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input
                    placeholder="e.g., Building A"
                    value={newSensor.location}
                    onChange={(e) => setNewSensor({ ...newSensor, location: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Department</Label>
                  <Input
                    placeholder="e.g., Warehouse"
                    value={newSensor.department}
                    onChange={(e) => setNewSensor({ ...newSensor, department: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Min Threshold</Label>
                  <Input
                    type="number"
                    placeholder="Alert if below"
                    value={newSensor.min_threshold}
                    onChange={(e) => setNewSensor({ ...newSensor, min_threshold: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Max Threshold</Label>
                  <Input
                    type="number"
                    placeholder="Alert if above"
                    value={newSensor.max_threshold}
                    onChange={(e) => setNewSensor({ ...newSensor, max_threshold: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={handleRegisterSensor} className="w-full mt-4">Register Sensor</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboard?.summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Radio className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.summary.total_sensors}</p>
                  <p className="text-sm text-gray-500">Total Sensors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Wifi className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.summary.online}</p>
                  <p className="text-sm text-gray-500">Online</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <WifiOff className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.summary.offline}</p>
                  <p className="text-sm text-gray-500">Offline</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.summary.warning}</p>
                  <p className="text-sm text-gray-500">Warning</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={dashboard.summary.active_alerts > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${dashboard.summary.active_alerts > 0 ? 'bg-red-200' : 'bg-red-100'}`}>
                  <Bell className={`w-5 h-5 ${dashboard.summary.active_alerts > 0 ? 'text-red-700' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.summary.active_alerts}</p>
                  <p className="text-sm text-gray-500">Active Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Active Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.slice(0, 5).map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={alert.severity === 'critical' ? 'destructive' : 'warning'}>
                      {alert.severity}
                    </Badge>
                    <div>
                      <p className="font-medium">{alert.message}</p>
                      <p className="text-sm text-gray-500">
                        {alert.sensor_name} • {alert.location} • {formatTime(alert.timestamp)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => acknowledgeAlert(alert.id)}
                  >
                    Acknowledge
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sensor Grid */}
      <Tabs defaultValue="grid">
        <TabsList>
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="table">Table View</TabsTrigger>
        </TabsList>
        
        <TabsContent value="grid" className="mt-4">
          <div className="grid grid-cols-4 gap-4">
            {sensors.map((sensor) => {
              const Icon = SENSOR_ICONS[sensor.type] || Radio;
              const colorClass = SENSOR_COLORS[sensor.type] || 'text-gray-500 bg-gray-100';
              
              return (
                <Card
                  key={sensor.sensor_id}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${selectedSensor?.sensor_id === sensor.sensor_id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSelectedSensor(sensor)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
                        <Icon className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
                      </div>
                      <Badge className={STATUS_COLORS[sensor.status] || STATUS_COLORS.online}>
                        {sensor.status}
                      </Badge>
                    </div>
                    <h3 className="font-medium truncate">{sensor.name}</h3>
                    <p className="text-3xl font-bold mt-2">
                      {formatValue(sensor.value, sensor.unit)}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <MapPin className="w-3 h-3" />
                      {sensor.location}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Last update: {formatTime(sensor.last_reading_at)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
            
            {sensors.length === 0 && (
              <div className="col-span-4 text-center py-12">
                <Radio className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No sensors registered</h3>
                <p className="text-gray-500">Register your first sensor to start monitoring</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="table" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sensor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Current Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Update</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sensors.map((sensor) => {
                  const Icon = SENSOR_ICONS[sensor.type] || Radio;
                  return (
                    <TableRow
                      key={sensor.sensor_id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelectedSensor(sensor)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium">{sensor.name}</p>
                            <p className="text-xs text-gray-500">{sensor.sensor_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{sensor.type?.replace('_', ' ')}</TableCell>
                      <TableCell>{sensor.location}</TableCell>
                      <TableCell className="font-mono font-medium">
                        {formatValue(sensor.value, sensor.unit)}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[sensor.status]}>{sensor.status}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-500">{formatTime(sensor.last_reading_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sensor Detail Modal */}
      {selectedSensor && (
        <Dialog open={!!selectedSensor} onOpenChange={() => setSelectedSensor(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {React.createElement(SENSOR_ICONS[selectedSensor.type] || Radio, { className: 'w-5 h-5' })}
                {selectedSensor.name}
              </DialogTitle>
              <DialogDescription>
                {selectedSensor.sensor_id} • {selectedSensor.location}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold">{formatValue(selectedSensor.value, selectedSensor.unit)}</p>
                  <p className="text-sm text-gray-500">Current Value</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Badge className={STATUS_COLORS[selectedSensor.status]} style={{ fontSize: '16px', padding: '8px 16px' }}>
                    {selectedSensor.status}
                  </Badge>
                  <p className="text-sm text-gray-500 mt-2">Status</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-lg font-medium">{formatTime(selectedSensor.last_reading_at)}</p>
                  <p className="text-sm text-gray-500">Last Update</p>
                </div>
              </div>
              
              {sensorHistory.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">24-Hour History</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={sensorHistory.slice().reverse()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(t) => new Date(t).toLocaleString()}
                          formatter={(v) => [formatValue(v, selectedSensor.unit), 'Value']}
                        />
                        <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default IoTSensorDashboard;
